/* This class is not a proxy of eventemitter3. It implements its own methods and properties.
 * Its public interface should not expose methods from the EventEmitter implementation used.
 */
import { EventEmitter } from "eventemitter3";
import { validateChannel } from "./validateChannel";
var ee = new EventEmitter();
;
export var InternalMessageBusChannelSetError = "Cannot alter readonly property [channels] of InternalMessageBus instance.";
/**
 * InternalMessageBus is an async in-memory msg bus based partially on NodeJS EventEmitter interface.
 * Supported in web browser via: https://github.com/primus/eventemitter3
 *
 * Channel is defined as a dot separated string - "channel.subChannel".
 * Listeners can be attached to both "channel" and "subChannel".
 * Channel and subChannel names are case-sensitive.
 *
 * Methods: on/once/off/emit are chainable.
 *
 */
var InternalMessageBus = /** @class */ (function () {
    /**
     * @constructor
     */
    function InternalMessageBus() {
        Object.defineProperties(this, {
            channels: {
                get: function () { return ee.eventNames(); },
                set: function (x) { throw new Error(InternalMessageBusChannelSetError); }
            }
        });
    }
    /**
     * on(...params) - attaches event listener to the specified event. To subscribe to the entire root channel you must listen for "channelName.*"
     * @param {STRING} channel
     * @param {InternalMessageBusListener} callback called with `InternalMessageBusMessage` object.
     * @param {ANY} context If you don't have access to ES6 syntax ()=>{} you can pass the optional object to be used for context.
     * @returns {InternalMessageBus} `InternalMessageBus instance`
     */
    InternalMessageBus.prototype.on = function (channel, callback, context) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            ee.on(channel, callback, context);
        }
        return this;
    };
    /**
     * once(...params) - attaches event listener to the specified event that happens only once. To subscribe to the entire root channel you must listen for "channelName.*"
     * @param {STRING} channel
     * @param {InternalMessageBusListener} callback called with `InternalMessageBusMessage` object.
     * @param {ANY} context If you don't have access to ES6 syntax ()=>{} you can pass the optional object to be used for context.
     * @returns {InternalMessageBus} `InternalMessageBus instance`
     */
    InternalMessageBus.prototype.once = function (channel, callback, context) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            ee.once(channel, callback, context);
        }
        return this;
    };
    /**
     * off(...params) - detaches event listener from the specified event. To unsubscribe to the entire root channel you must listen for "channelName.*"
     * @param {STRING} channel
     * @param {InternalMessageBusListener} callback called with `InternalMessageBusMessage` object.
     * @param {ANY} context If you don't have access to ES6 syntax ()=>{} you can pass the optional object to be used for context.
     * @returns {InternalMessageBus} `InternalMessageBus instance`
     */
    InternalMessageBus.prototype.off = function (channel, callback, context) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            ee.off(channel, callback, context);
        }
        return this;
    };
    /**
     * emit(...params) - emits message on a certain channel. All channels that match the pattern are going to be notified.
     * If optional context is passed during .on/.once the function is going to be called with it.
     * @param {STRING} channel
     * @param {ANY} data
     * @returns {InternalMessageBus} `InternalMessageBus instance`
     */
    InternalMessageBus.prototype.emit = function (channel) {
        var data = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            data[_i - 1] = arguments[_i];
        }
        /* istanbul ignore next */ /* all cases covered with tests */
        if (channel.match(/\*/gim)) {
            throw new Error("You cannot post to all.");
        }
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            var subChannel = channel.split(".");
            var mainChannel = subChannel.shift();
            var tempChannels = subChannel
                .reduce(function (a, b, i) {
                a.push(a[i] + "." + b) && a;
                return a;
            }, [mainChannel]);
            var allChannels = [].concat(tempChannels.map(function (x) { return x + ".*"; }), tempChannels.map(function (x) { return x + "*"; }), [channel]);
            var msg_1 = { channel: mainChannel, subChannel: subChannel, data: data };
            allChannels.forEach(function (x) { return setTimeout(function () { return ee.emit(x, msg_1); }, 0); });
        }
        return this;
    };
    /**
     * listeners(...params) - returns all listeners per provided channel.
     * @param {STRING} channel
     * @returns {InternalMessageBusListener[]} `InternalMessageBusListener[]`
     */
    InternalMessageBus.prototype.listeners = function (channel) {
        /* istanbul ignore next */ /* all cases covered with tests */
        if (validateChannel(channel)) {
            return ee.listeners(channel);
        }
        /* istanbul ignore next */ /* all cases covered with tests */
        return [];
    };
    return InternalMessageBus;
}());
export { InternalMessageBus };
//# sourceMappingURL=InternalMessageBus.js.map